import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const SettingsContext = createContext(null);
const STORAGE_KEY = "ratesight_fb_ads_ai_settings";
const MODEL_OPTIONS = [
  { value: "anthropic/claude-4.5-sonnet", label: "Claude 4.5 Sonnet" },
  { value: "google/gemini-2.5-flash-image-preview", label: "Gemini 2.5 Flash Image Preview" },
  { value: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "google/gemini-3.0-pro", label: "Gemini 3.0 Pro" },
  { value: "openai/gpt-5", label: "GPT-5" },
  { value: "openai/gpt-5.1", label: "GPT 5.1" },
  { value: "xai/grok-4.1", label: "Grok 4.1" },
  { value: "xai/grok-4", label: "Grok 4 Fast" }
]; // Keep in sync with edge-function ALLOWED_MODELS lists

const defaultSettings = {
  preferredAiModel: MODEL_OPTIONS[0].value
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultSettings;
      const parsed = JSON.parse(raw);
      return { ...defaultSettings, ...parsed };
    } catch (error) {
      console.warn("SETTINGS_CONTEXT: Failed to parse stored settings", error);
      return defaultSettings;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn("SETTINGS_CONTEXT: Failed to persist settings", error);
    }
  }, [settings]);

  const value = useMemo(
    () => ({
      preferredAiModel: settings.preferredAiModel,
      setPreferredAiModel: (model) =>
        setSettings((prev) => ({ ...prev, preferredAiModel: model })),
      modelOptions: MODEL_OPTIONS
    }),
    [settings.preferredAiModel]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};